import * as Xstate from "@xstate/react";
import classNames from "classnames";
import DOMPurify from "dompurify";
import React, { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { GlobalContext } from "../../../app/stateMachines/GlobalContext";
import Button from "../../../components/button/Button";
import RichText from "../../../components/richtext/RichText";
import { ContactNoteModel } from "../../../factories/contacts/models/contact-note.models";
import {
  useAddContactNoteMutation,
  useUpdateContactNoteMutation,
} from "../../../slices/ContactNotesSlice";
import { useUpdateWorkNoteMutation } from "../../../slices/WorkNotesSlice";
import { serverMessage } from "../../../utils/serverMessage";
import { NOTE_SAVE_FAILED } from "./noteSave";

type Props = {
  isActive: boolean;
  onClose: () => void;
  defaultValues?: ContactNoteModel;
  contactId?: string;
  businessContactId?: string;
  isWork?: boolean;
};

type FormData = {
  title: string;
  note: string;
};

export const ContactNoteModal: FC<Props> = ({
  isActive,
  onClose,
  defaultValues,
  contactId,
  isWork,
}) => {
  const { userInfoService } = useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;

  const [
    addContactNote,
    { isLoading: isLoadingContactAdd, isSuccess: isSuccessContactAdd },
  ] = useAddContactNoteMutation();
  const [
    updateContactNote,
    { isLoading: isLoadingContactUpdate, isSuccess: isSuccessContactUpdate },
  ] = useUpdateContactNoteMutation();
  const [
    updateWorkNote,
    { isLoading: isLoadingWorkUpdate, isSuccess: isSuccessWorkUpdate },
  ] = useUpdateWorkNoteMutation();

  const { register, handleSubmit, control } = useForm<FormData>({
    defaultValues: defaultValues,
  });

  const isLoadingUpdate = isLoadingContactUpdate || isLoadingWorkUpdate;
  // The server's answer when saving fails (QA batch 14: the window stayed open silently).
  const [saveError, setSaveError] = useState<string | null>(null);

  const onSubmit = async (data: FormData) => {
    if ((data.note || data.title) && !isLoadingContactAdd && !isLoadingUpdate) {
      setSaveError(null);
      const safeNote = DOMPurify.sanitize(data.note || "");
      // Rows of the client Notes tab (search-notes) carry their id as `noteId`.
      const noteId = defaultValues?.id || defaultValues?.noteId || "";
      try {
        if (defaultValues) {
          if (isWork) {
            await updateWorkNote({
              note: safeNote,
              title: data.title,
              createdAt: defaultValues.createdAt,
              createdByUser: defaultValues.createdByUser,
              createdByUserId: defaultValues.createdByUserId,
              id: noteId,
              workId: defaultValues.workId,
              isPinned: defaultValues.isPinned,
              modifiedByUser: defaultValues.modifiedByUser,
              relayId: defaultValues.relayId,
              updatedAt: defaultValues.updatedAt,
            }).unwrap();
          } else {
            await updateContactNote({
              note: safeNote,
              title: data.title,
              id: noteId,
            }).unwrap();
          }
        } else {
          await addContactNote({
            title: data.title,
            note: safeNote,
            createdByUserRelayId: `${userInfoByEmail?.id}`,
            contactId: contactId || "",
          }).unwrap();
        }
      } catch (error) {
        setSaveError(serverMessage(error, NOTE_SAVE_FAILED));
      }
    }
  };

  useEffect(() => {
    if (isSuccessContactAdd || isSuccessContactUpdate || isSuccessWorkUpdate) {
      onClose();
    }
  }, [isSuccessContactAdd, isSuccessContactUpdate, isSuccessWorkUpdate]);

  return (
    <div
      className={classNames(
        "modal !z-[64]",
        isActive ? "modal-open" : "modal-close",
      )}>
      <div className={"modal-box p-0"}>
        <div
          className={
            "border-b border-gray-300 px-5 py-4 text-center text-lg font-bold"
          }>
          {defaultValues ? "Update" : "Add"} note
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={"px-5 py-4"}>
          <div className={"mb-4"}>
            <input
              type={"text"}
              {...register("title", {})}
              className={"input input-bordered w-full"}
              placeholder={"Title"}
            />
          </div>
          <div>
            <Controller
              name={"note"}
              control={control}
              defaultValue={defaultValues?.note || ""}
              render={({ field }) => (
                <RichText
                  value={field.value || ""}
                  onChange={field.onChange}
                  outerHeight={200}
                  toolbarHeight={50}
                  className={"w-full"}
                />
              )}
            />
          </div>
          {saveError && (
            <div role={"alert"} className={"mt-3 text-sm text-red-600"}>
              {saveError}
            </div>
          )}
          <div className={"mt-4 flex justify-end gap-4"}>
            <Button
              label={defaultValues ? "Update" : "Add"}
              buttonType={"submit"}
              disabled={isLoadingContactAdd || isLoadingUpdate}
            />
            <Button label={"Cancel"} onClick={onClose} colorType={"outline"} />
          </div>
        </form>
      </div>
    </div>
  );
};
